import { MILE } from "@/config/const.js";
import { GeoUtil } from "@/utils";
import {
  area,
  bbox,
  buffer,
  center,
  centerOfMass,
  distance,
  lineString,
  mask,
  point,
  polygon,
} from "@turf/turf";
import { isNumber, isString } from "lodash";
import moment from "moment-timezone";

const MAX_ALLOWED_HOURS = 2; // number
const MAX_ALLOWED_AREA = 12 * MILE * 12 * MILE; // 12NM * 12NM
const DEFAULT_LENGTH_UNITS = "nauticalmiles"; // string
const SPECIFY_PLAYBACK_AREA_MODE = "specify_playback_area";
const BUFFERED_AREA_RADIUS = 0.5; // number
const DEFAULT_BUFFERED_AREA_RADIUS = 0.51; // number、初期の面積が1.0nm^2になるように調整

export class RgPlaybackSettings {
  constructor({
    north_latitude, // : string
    east_longitude, // : string
    south_latitude, // : string
    west_longitude, // : string
    started_at, // : string
    ended_at, // : string
    zoom, // : string
  } = {}) {
    this._eastLongitude = Number(east_longitude);
    this._northLatitude = Number(north_latitude);
    this._southLatitude = Number(south_latitude);
    this._westLongitude = Number(west_longitude);
    this._startedAt = started_at;
    this._endedAt = ended_at;
    this._zoom = Number(zoom);
  }

  static fromCenterCoordinate({
    longitude, // : number,
    latitude, // : number
  }) {
    const centerPoint = point([longitude, latitude]);
    const buffered = buffer(centerPoint, DEFAULT_BUFFERED_AREA_RADIUS, {
      units: DEFAULT_LENGTH_UNITS,
    });
    const bboxResult = bbox(buffered); // [west: number; south: number; east: number; north: number]
    return new RgPlaybackSettings({
      north_latitude: bboxResult[3],
      east_longitude: bboxResult[2],
      south_latitude: bboxResult[1],
      west_longitude: bboxResult[0],
    });
  }

  static fromCoordinates(
    coordinates, // : Array<[longitude, latitude]>
    zoom = null // : number | null
  ) {
    const bboxResult = bbox(lineString(coordinates)); // [west: number; south: number; east: number; north: number]
    return new RgPlaybackSettings({
      north_latitude: bboxResult[3],
      east_longitude: bboxResult[2],
      south_latitude: bboxResult[1],
      west_longitude: bboxResult[0],
      zoom,
    });
  }

  // boolean
  static isPointValid(longitude, latitude) {
    return (
      GeoUtil.isValidLongitude(longitude) && GeoUtil.isValidLatitude(latitude)
    );
  }

  // boolean
  isEqual(
    other // :RgPlaybackSettings
  ) {
    if (!(other instanceof RgPlaybackSettings)) return false;
    return (
      this._eastLongitude === other._eastLongitude &&
      this._northLatitude === other._northLatitude &&
      this._southLatitude === other._southLatitude &&
      this._westLongitude === other._westLongitude &&
      this._startedAt === other._startedAt &&
      this._endedAt === other._endedAt &&
      this._zoom === other._zoom
    );
  }

  isNorthEastPointValid() {
    // bool
    return RgPlaybackSettings.isPointValid(...this.calcNorthEastCoordinate());
  }

  isSouthWestPointValid() {
    // bool
    return RgPlaybackSettings.isPointValid(...this.calcSouthWestCoordinate());
  }

  isAreaCoordinateValid() {
    return this.isNorthEastPointValid() && this.isSouthWestPointValid();
  }

  // mapboxのcoordinateに合わせた形式で緯度経度を返す
  // [longitude, latitude]
  calcNorthEastCoordinate() {
    // array
    return [this._eastLongitude, this._northLatitude];
  }

  // [longitude, latitude]
  calcSouthEastCoordinate() {
    // array
    return [this._eastLongitude, this._southLatitude];
  }

  // [longitude, latitude]
  calcSouthWestCoordinate() {
    // array
    return [this._westLongitude, this._southLatitude];
  }

  // [longitude, latitude]
  calcNorthWestCoordinate() {
    // array
    return [this._westLongitude, this._northLatitude];
  }

  calcCoordinates() {
    // array of coordinate
    // 北東、南東、南西、北西の順
    return [
      this.calcNorthEastCoordinate(), // [longitude, latitude]
      this.calcSouthEastCoordinate(), // [longitude, latitude]
      this.calcSouthWestCoordinate(), // [longitude, latitude]
      this.calcNorthWestCoordinate(), // [longitude, latitude]
    ];
  }

  calcBufferedBoundingBox(
    {
      radius, // number,
      units, // string
    } = { radius: BUFFERED_AREA_RADIUS, units: DEFAULT_LENGTH_UNITS }
  ) {
    // array of number [west: number; south: number; east: number; north: number]
    // 枠の外に出る船の描画のため、少し広めにAPIリクエストする。
    const buffered = buffer(this.calcPolygon(), radius, {
      units: units,
    });
    return bbox(buffered); // [west: number; south: number; east: number; north: number]
  }

  calcPoints() {
    // array of Point
    // 北東、南東、南西、北西の順
    return [
      // point()
      point(this.calcNorthEastCoordinate(), { id: "north-east-area-point" }),
      point(this.calcSouthEastCoordinate(), { id: "south-east-area-point" }),
      point(this.calcSouthWestCoordinate(), { id: "south-west-area-point" }),
      point(this.calcNorthWestCoordinate(), { id: "north-west-area-point" }),
    ];
  }

  // ポリゴンの情報を生成して返す
  calcPolygon() {
    if (!this.isAreaCoordinateValid()) return null;
    return polygon([
      [
        ...this.calcCoordinates(), // 北東、南東、南西、北西の順
        this.calcNorthEastCoordinate(), // 最後に始点を指定する
      ],
    ]);
  }

  calcMask() {
    if (!this.isAreaCoordinateValid()) return null;
    return mask(this.calcPolygon());
  }

  // 重心点
  calcCenterOfMass() {
    if (!this.isAreaCoordinateValid()) return null;
    return centerOfMass(this.calcPolygon());
  }

  // 中心点
  calcCenter() {
    if (!this.isAreaCoordinateValid()) return null;
    return center(this.calcPolygon());
  }

  // 面積
  calcArea() {
    // return value m**2
    // 1NM = 1,852m
    if (!this.isAreaCoordinateValid()) return null;
    return area(this.calcPolygon());
  }

  // 面積が最大を超えていないか判定
  isAreaValid = (maxAllowedArea = MAX_ALLOWED_AREA) => {
    if (!this.isAreaCoordinateValid()) return false;
    return this.calcArea() <= maxAllowedArea;
  };

  calcHeight = (
    units = DEFAULT_LENGTH_UNITS // string
  ) => {
    return distance(
      this.calcNorthEastCoordinate(),
      this.calcSouthEastCoordinate(),
      { units }
    );
  };
  calcWidth = (
    units = DEFAULT_LENGTH_UNITS // string
  ) => {
    return distance(
      this.calcNorthEastCoordinate(),
      this.calcNorthWestCoordinate(),
      { units }
    );
  };
  isDateStringValid = dateString => {
    // check like "2024-01-01 00:00:00"
    if (!isString(dateString)) return false;
    return moment(dateString, moment.ISO_8601, true).isValid();
  };
  isStartedAtValid = () => {
    return this.isDateStringValid(this._startedAt);
  };
  isEndedAtValid = () => {
    return this.isDateStringValid(this._endedAt);
  };
  isPeriodValid = () => {
    if (!this.isStartedAtValid()) return false;
    if (!this.isEndedAtValid()) return false;
    return (
      Math.abs(
        moment(this._startedAt).diff(moment(this._endedAt), "minutes")
      ) <=
      MAX_ALLOWED_HOURS * 60
    );
  };

  calcPeriod = () => {
    if (!this.isPeriodValid()) return null;
    return {
      startedAt: moment(this._startedAt).toDate(), // Date
      endedAt: moment(this._endedAt).toDate(), // Date
    };
  };

  calcPeriodISOString = () => {
    if (!this.isPeriodValid()) return null;
    const {
      startedAt: startedAtDate,
      endedAt: endedAtDate,
    } = this.calcPeriod();
    return {
      startedAt: startedAtDate.toISOString(), // string
      endedAt: endedAtDate.toISOString(), // string
    };
  };

  isZoomValid = (
    // zoom, // :number
    {
      maxZoom, // :number
      minZoom, // :number
    } = {
      maxZoom: 18,
      minZoom: 0,
    }
  ) => {
    const { _zoom: zoom } = this;
    if (!isNumber(zoom)) return false;
    if (!isNumber(maxZoom)) return false;
    if (!isNumber(minZoom)) return false;
    return minZoom <= zoom && zoom <= maxZoom;
  };

  // エリア指定のmapに戻る時のリクエストパラメータを返す
  calcSpecifyAreaSettingParams = () => {
    return {
      mode: SPECIFY_PLAYBACK_AREA_MODE,
      east_longitude: this._eastLongitude,
      north_latitude: this._northLatitude,
      west_longitude: this._westLongitude,
      south_latitude: this._southLatitude,
      ...(this.isZoomValid() ? { zoom: this._zoom } : {}),
    };
  };

  // 履歴画面に遷移するときのリクエストパラメータを返す
  calcPlaybackRequestSettingParams = () => {
    // object
    const { startedAt: started_at, endedAt: ended_at } = this.isPeriodValid()
      ? this.calcPeriodISOString()
      : {};
    return {
      east_longitude: this._eastLongitude,
      north_latitude: this._northLatitude,
      west_longitude: this._westLongitude,
      south_latitude: this._southLatitude,
      ...(this.isPeriodValid() ? { started_at, ended_at } : {}),
      ...(this.isZoomValid() ? { zoom: this._zoom } : {}),
    };
  };

  // mapに戻る時のMapを初期化する設定を返す
  calcSpecifyAreaMapSetting = () => {
    return {
      ...(this.isAreaValid()
        ? { center: this.calcCenterOfMass().geometry.coordinates }
        : {}),
      ...(this.isZoomValid() ? { zoom: this._zoom } : {}),
    };
  };

  static calcFarthestPointWithPoints = (point, points) => {
    if (!point) return null;
    if (!Array.isArray(points)) return null;
    let maxDistance = distance(point, points[0]);
    return points.reduce((acc, current) => {
      const currentDistance = distance(point, current);
      if (maxDistance < currentDistance) {
        maxDistance = currentDistance;
        return current;
      }
      return acc;
    });
  };

  calcFarthestPoint(
    point // :[longitude, latitude]
  ) {
    return RgPlaybackSettings.calcFarthestPointWithPoints(
      point,
      this.calcPoints()
    );
  }

  calcFarthestLineString(
    point // :[longitude, latitude]
  ) {
    const farthestPoint = this.calcFarthestPoint(point);
    if (!farthestPoint) return null;
    return lineString([point, farthestPoint.geometry.coordinates]);
  }

  toAreaSetting = (
    router, // : VueRouter
    nextRouteName = "enterprise-live-map"
  ) => {
    if (!router) return; // VueRouterInstanceが渡されないときは何もしない
    if (!nextRouteName) return;
    router.push({
      name: nextRouteName,
      query: this.calcSpecifyAreaSettingParams(),
    });
  };

  // エリア指定で、かつデフォルトのマップ設定でライブマップに遷移
  toAreaSettingWithDefaultPosition = (
    router, // : VueRouter
    nextRouteName = "enterprise-live-map"
  ) => {
    if (!router) return; // VueRouterInstanceが渡されないときは何もしない
    if (!nextRouteName) return;
    router.push({
      name: nextRouteName,
      query: {
        mode: SPECIFY_PLAYBACK_AREA_MODE,
      },
    });
  };

  toDefaultMap = (
    router, // : VueRouter
    nextRouteName = "enterprise-live-map"
  ) => {
    if (!router) return; // VueRouterInstanceが渡されないときは何もしない
    if (!nextRouteName) return;
    router.push({
      name: nextRouteName,
    });
  };

  toPlayback = (
    router, // : VueRouter
    nextRouteName = "enterprise-rg-object-history-player"
  ) => {
    if (!router) return; // VueRouterInstanceが渡されないときは何もしない
    if (!nextRouteName) return;
    router.push({
      name: nextRouteName,
      query: this.calcPlaybackRequestSettingParams(),
    });
  };

  isModeSpecifyPlaybackArea = (
    router // : VueRouter
  ) => {
    if (!router) return false;
    return router.currentRoute.query.mode === SPECIFY_PLAYBACK_AREA_MODE;
  };
}

export class RgPlaybackService {
  createRgPlaybackSettings(constructorParams = {}) {
    return new RgPlaybackSettings(constructorParams);
  }

  // 中心点の緯度経度からエリア指定の初期設定を生成して返す
  createRgPlaybackDefaultSettings({
    longitude, // :number
    latitude, // :number
  } = {}) {
    if (!isNumber(longitude)) return null;
    if (!isNumber(latitude)) return null;
    return RgPlaybackSettings.fromCenterCoordinate({ longitude, latitude });
  }

  // 2点の緯度経度からエリア指定の初期設定を生成して返す
  createRgPlaybackSettingsFromCoordinates(
    coordinates, // : Array<[longitude, latitude]>
    zoom = null // : number | null
  ) {
    if (!Array.isArray(coordinates)) return null;
    if (coordinates.some(coordinate => !Array.isArray(coordinate))) {
      return null;
    }
    if (coordinates.some(coordinate => coordinate.length !== 2)) return null;
    if (coordinates.flat(2).some(geoInfo => !isNumber(geoInfo))) return null;
    if (coordinates.length !== 2) return null;

    return RgPlaybackSettings.fromCoordinates(coordinates, zoom);
  }
}

export default new RgPlaybackService();
